import React, { useEffect, useRef, useState } from 'react';
import { ArticleItem } from '../../../lib/algolia/types'; // Adjust path
import styled, { StylesProps, cx } from '../../../lib/styles'; // Adjust path
import articleTileBlockStyles from './ArticleTileBlock_styles';
import { compose } from 'recompose';
import { decodeHtmlEntities } from '../../../lib/stringUtils';
import dayjs from 'dayjs';
import _ from 'lodash';

type TextPosition = 'left' | 'right' | 'bottom' | 'overlay';
type TileDirection = 'row' | 'column';

/**
 * Configuration for the article tile block.
 * Defines how the articles should be displayed in columns on desktop viewport. Mobile viewports will be handled according the viewport rules.
 * Properties:
 * - width: Width of the column (e.g., '50%', '100%')
 * - tileDirection: Direction of the tiles within the column ('row' or 'column')
 * - articles: Array of article configurations to display in this column
 * - columnConfig: Optional configuration for the column (e.g., showTileMeta, showColumnNav)
 * - cssClasses: Optional CSS classes for styling the column
 */
export interface ArticleTileColumn {
	width: string;
	tileDirection: TileDirection;
  articles: ArticleTileConfig[];
	columnConfig?: {
		showTileMeta?: boolean; // Optional flag to hide metadata
		showColumnNav?: boolean; // Optional flag to show navigation link at the bottom of the
	},
	cssClasses?: string;
}

/**
 * Configuration for each article tile.
 * Properties:
 * - article: The article data to display
 * - isCoverImage: Optional flag to indicate if this tile is a cover image
 * - textPosition: Position of the text relative to the image ('left', 'right', 'bottom', 'overlay')
 * - tileWidth: Optional width for the tile (e.g., '100%', '50%')
 * - cssClasses: Optional CSS classes for styling the tile
 * - styles: Optional styles for the tile
 */
export interface ArticleTileConfig {
	article: ArticleItem; // Single article configuration
	options?: {
		textPosition: TextPosition;
		isCoverImage?: boolean; // Optional flag for cover image
		tileWidth?: string; // Optional width for the tile
	},
	cssClasses?: string; // Optional CSS classes for styling
	styles?: any; // Optional styles for the tile
}

/**
 * Subcategory configuration for the article tile block.
 * Used to filter articles by subcategories and also configuration for the Tile heading.
 * Properties:
 * - id: Optional unique identifier for the subcategory
 * - label: Display label for the subcategory
 * - url: Optional URL for the subcategory link
 * - cssClasses: Optional CSS classes for styling the subcategory
 */
interface ArticleSubCategory {
	id?: string;
	label: string;
	url?: string;
	cssClasses?: string;
}

/**
 * Props for the ArticleTileBlock component.
 * @param title - Optional title for this specific block
 * @param columns - Array of columns to display articles in
 * @param subCategories - Optional subcategories to filter articles
 * @param blockConfig - Optional configuration for the block
 * @param cssClasses - Optional CSS classes for styling
 */
export interface ArticleTileBlockProps {
	title?: ArticleSubCategory; // Optional title for this specific block
	columns: ArticleTileColumn[],
	subCategories?: ArticleSubCategory[]; // Optional subcategories to filter articles
	blockConfig?: {
		showTitleLinks?: boolean; // Optional flag to show title links
	},
	cssClasses?: string; // Optional CSS classes for styling
}

/**
 * Component to render a single article tile.
 * @param article - The article data to display
 * @param textPosition - Position of the text relative to the image
 * @param isCoverImage - Whether this tile is a cover image
 * @param tileWidth - Optional width for the tile
 * @returns JSX element representing the article tile
 */
const ArticleTileItem = ({
	article,
	options,
	styles,
}: ArticleTileConfig & StylesProps<typeof articleTileBlockStyles>) => {
	const { 
		textPosition = 'bottom', 
		isCoverImage = false, 
		tileWidth = '100%' 
	} = options || {};

	useEffect(() => {
		// console.log('ArticleTileItem mounted with article:', { article , textPosition, isCoverImage, tileWidth });
	}, [article]);

	const articleDate = dayjs(article.post_date).format('MMM D, YYYY');

	return (
		<a 
			href={article.slug} 
			className={cx(
				`article-tile-link tile-text-${textPosition}`,
				{ 
					'is-cover-image': isCoverImage, 
					'use-grid-on-resize': textPosition === 'left' || textPosition === 'right' 
				},
				styles.articleTileLink,
			)}
			aria-label={`Read more about ${article.title}`}
			style={{
				maxWidth: tileWidth,
				width: `calc(${tileWidth} - 0.5em)`, 
				flex: `1 0 calc(${tileWidth} - 0.5em)`,
				position: isCoverImage ? 'relative' : 'static',
			}}
		>
			<div
				className={cx(`article-tile-wrap`, styles.articleTileWrap)}
				style={{
					minHeight: isCoverImage ? '250px' : '100px',
					flexDirection: textPosition === 'bottom' ? 'column' :
						textPosition === 'right' ? 'row' : 'row-reverse',
					overflow: 'hidden',
					position: isCoverImage ? 'relative' : 'static',
					background: isCoverImage ? `url(${article.image}) no-repeat center center` : 'none',
					backgroundSize: isCoverImage ? 'cover' : 'contain',
				}}
			>
				{!isCoverImage &&
					<div 
						className={cx(`article-tile-image-wrap`, styles.articleTileImageWrap)}
						style={{ 
							width: textPosition === 'bottom' ? '100%' : '50%' 
						}}
					>
						<img 
							src={article.image} 
							alt={article.title} 
							className={cx(`article-tile-image img-fluid`, styles.articleTileImage)}
						/>
					</div>
				}
				<div 
					className={cx(`d-flex article-tile-content`, styles.articleTileContent)}
					style={{
						position: isCoverImage ? 'absolute' : 'static',
						width: isCoverImage || textPosition === 'bottom' ? '100%' : '50%',
						bottom: isCoverImage ? '0' : 'auto',
						left: isCoverImage ? '0' : 'auto',
						right: isCoverImage ? '0' : 'auto',
						padding: isCoverImage ? '0 0.5em 0.5em' : '0',
					}}
				>
					<p 
						className={cx(`article-tile-content-title`, styles.articleTileContentTitle)}
						style={{
							fontSize: textPosition === 'left' || textPosition === 'right' ? '14px' : undefined,
						}}
					>
						{article.title}
					</p>
					<div className={cx(`article-tile-content-meta`, styles.articleTileContentMeta)}>
						<span className={cx(`article-tile-author`, styles.articleTileAuthor)}>{article.author.name}</span>
						<span className={cx(`article-tile-post_date`, styles.articleTilePostDate)}>{articleDate}</span>
					</div>
				</div>
			</div>
		</a>
	)
};

/**
 * Render a menu for displaying additional links when there are more than 5 subcategories.
 * This component handles both desktop hover and mobile click interactions.
 * It uses a timer to delay closing the menu on hover out.
 * @param subCategories - Array of subcategories to display in the menu.
 * @param classNames - Optional additional CSS classes for styling the menu container.
 * @returns JSX.Element
 */
const AllLinksOverflowMenu = ({ 
	subCategories ,
	classNames = '',
}: {
	subCategories: ArticleSubCategory[],
	classNames?: string,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const timerRef = useRef<number | null>(null); // Ref to store the timer ID
  const menuRef = useRef<HTMLDivElement | null>(null); // Ref to the container element

  // Clear the timer whenever the component unmounts
  useEffect(() => {
    return () => {
      // Check if timerRef.current has a value before trying to clear it
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const openMenu = () => {
    // Check if timerRef.current has a value before trying to clear it
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setIsMenuOpen(true);
  };

  // Handler to close the menu with a delay
  const closeMenuDelayed = () => {
    // Set a timeout to close the menu after 1 second
    timerRef.current = setTimeout(() => {
      setIsMenuOpen(false);
    }, 3000) as unknown as number;
  };

  // Handler to toggle the menu on mobile/click
  const toggleMenu = (event) => {
    event.stopPropagation(); // Prevent the document click listener from firing immediately
    if (isMenuOpen) {
      setIsMenuOpen(false);
    } else {
      openMenu();
    }
  };

  // Close the menu if a click happens outside the container
  useEffect(() => {
    const handleOutsideClick = (event) => {
      // Check if the click is outside the menu container
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    
    // Add the event listener when the menu is open
    if (isMenuOpen) {
      document.addEventListener('click', handleOutsideClick);
    }
    
    // Clean up the event listener when the menu is closed or component unmounts
    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [isMenuOpen]);

  return (
    <div
      ref={menuRef}
      className={cx(classNames,{
        'menu-is-open': isMenuOpen,
      })}
      // Desktop: Open menu on hover, close with a delay
      onMouseEnter={openMenu}
      onMouseLeave={closeMenuDelayed}
      // Mobile: Toggle menu on click
      onClick={toggleMenu}
    >
      <a className="nav-link more-link" aria-label="See more categories" href={undefined}>More&nbsp;<i className="las la-angle-down"></i></a>
      {isMenuOpen && (
        <div className="overflow-menu">
          {subCategories && subCategories.map((subCategory, index) => (
            <a 
              key={index}
              href={subCategory.url} 
              className="nav-link"
              aria-label={`Read more about ${decodeHtmlEntities(subCategory.label)}`}
            >
              {decodeHtmlEntities(subCategory.label)}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Component to render a block of article tiles in customisable columns.
 * @param title - Optional title for the block.
 * @param columns - Array of columns to display articles in.
 * @param subCategories - Optional subcategories to filter articles.
 * @param blockConfig - Optional configuration for the block.
 * @param cssClasses - Optional CSS classes for styling.
 * @returns JSX element representing the article tiles.
 */
const ArticleTileBlock: React.FC<ArticleTileBlockProps> = ({
  title,
  columns = [],
	subCategories = [],
	blockConfig = {},
	cssClasses = '',
  styles
}: ArticleTileBlockProps & StylesProps<typeof articleTileBlockStyles>) => {
	const { showTitleLinks = false } = blockConfig;
	// useEffect(() => {
	// 	// console.log('FeatureArticleTileBlock mounted with articles:', { title, styles, columns, subCategories });
	// }, [columns]);

	if (!columns || columns.length === 0) {
		console.warn('FeatureArticleTileBlock: No layout to display');
		return null; // Return null if no articles are available
	}
	
  return (
		<div className={cx(`article-tile-block content-block`, 
			cssClasses, styles.articleTileBlock,
		)}>
			{title && showTitleLinks && (
				<>
					<div className={cx(`nav-section-links ${title.cssClasses}`, styles.navSectionLinks)}>
						{title?.label && (
							<h2><a href={title?.url || undefined} aria-label={`Read more about ${decodeHtmlEntities(title.label)}`}>{title.label}</a></h2>
						)}
						{subCategories && subCategories.length > 0 && (
							<>
								<div className={cx(`nav-section-links-items`, styles.navSectionLinksItems)}>
									{subCategories && subCategories.slice(0,4).map((subCategory, index) => (
										<a 
											key={index}
											href={subCategory.url}
											className={cx(`nav-link`)}
											aria-label={`Read more about ${decodeHtmlEntities(subCategory.label)}`}
										>
											{decodeHtmlEntities(subCategory.label)}
										</a>
									))}
								</div>
								{subCategories.length > 5 && (
									<AllLinksOverflowMenu 
										subCategories={subCategories} 
										classNames={cx(`more-menu-container`, styles.moreMenuContainer)}
									/>
								)}
							</>
						)}
					</div>
				</>
			)}
			<div className={cx(`article-tile-block-grid`, 
				cssClasses, styles.articleTileBlockGrid,
			)}>
			{columns.map((column, index) => {
				const { columnConfig: { 
					showColumnNav = false 
				} = {} } = column;
				return (
					<div 
						key={index} 
						className={cx(
							`article-tile-block-grid-column article-tile-block-grid-column-${index}`, 
							column.cssClasses, styles.articleTileBlockGridColumn
						)}
						style={{ 
							width: column.width || '100%' 
						}}
					>
						{column.articles.map((articleConfig, articleIndex) => {
							const { options: {
								textPosition = 'bottom',
								isCoverImage = false,
								tileWidth = '100%',
							} = {} } = articleConfig;
							return (
								<ArticleTileItem
									key={articleIndex}
									article={articleConfig.article}
									options={{
										textPosition,
										isCoverImage,
										tileWidth,
									}}
									cssClasses={articleConfig.cssClasses}
									styles={styles}
								/>
							)
						})}
						{showColumnNav && title && title.url && (
							<div className={cx(`column-nav-link`)}>
								{title && showTitleLinks && (
									<a href={title?.url || undefined} className={cx(``)}
										aria-label={`Read more about ${decodeHtmlEntities(title.label)}`}
									>
										See more {decodeHtmlEntities(title.label)}&nbsp;<i className="las la-angle-right"></i>
									</a>
								)}
							</div>
						)}
					</div>
				)
			})}
			</div>
		</div>
	);
};

export default compose(styled(articleTileBlockStyles))(ArticleTileBlock);